import React, { useState } from 'react';
import FeatureTrackingService from '@/services/analytics/feature-tracking-service';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (...args: any[]) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  outline?: boolean;
  text?: boolean;
  delete?: boolean;
  hidden?: boolean;
  className?: string;
  style?: React.CSSProperties;
  tooltipContent?: any;
  analyticsFeature?: string;
  analyticsPage?: string;
  analyticsEventName?: string;
  analyticsEventProperties?: Record<string, string | number | boolean | null | undefined>;
  [key: string]: any; // To allow additional props
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  type = 'button',
  disabled = false,
  outline = false,
  text = false,
  delete: isDelete = false,
  hidden = false,
  className = '',
  style = {},
  tooltipContent = null,
  analyticsFeature = '',
  analyticsPage = '',
  analyticsEventName,
  analyticsEventProperties,
  ...props
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Analytics attributes for PostHog autocapture events like rageclicks
  const analyticsAttributes = analyticsFeature
    ? {
        'data-ph-capture-attribute-feature_name': analyticsFeature,
        ...(analyticsPage && { 'data-ph-capture-attribute-page': analyticsPage }),
        'data-ph-capture-attribute-interaction_type': 'click',
      }
    : {};

  // Layout styles
  const layoutStyles = [
    'flex flex-row justify-center items-center',
    !className.includes('h-') && 'h-11',
    !className.match(/\b(p-|px-|py-|pl-|pr-|pt-|pb-)\w+/) && !text && 'px-[14px] py-2',
    !className.includes('rounded') && 'rounded-lg',
  ];

  // Typography styles
  const typographyStyles = ['font-semibold text-base leading-6 tracking-[0%]'];

  // Background and color styles
  const colorStyles = [
    // Base background color
    !className.includes('bg') && (isDelete ? 'bg-failure' : 'bg-primary'),
    // Text color for non-outline/text buttons
    !outline && !text && 'text-text_contrast',
  ];

  // Hover styles
  const hoverStyles = [
    !className.includes('hover') && 'hover:opacity-90',
    !outline &&
      !text &&
      (isDelete ? 'hover:bg-failure_highlight' : 'hover:bg-[--color-brand-hover]'),
  ];

  // Disabled styles
  const disabledStyles = [
    // For text buttons, use disabled text color
    text && 'disabled:text-disabled',
    // For standard buttons (not text, outline, or delete), use primary_highlight in light mode and disabled in dark mode
    !text &&
      !outline &&
      !isDelete &&
      'disabled:bg-[color-mix(in_srgb,var(--color-brand-highlight)_35%,#F3F3F3)] dark:disabled:bg-disabled',
    // For all other buttons (outline, delete), use disabled background
    isDelete && 'disabled:bg-disabled',
    outline && 'disabled:opacity-50',
    // Add disabled cursor unless overridden
    !className.includes('cursor') && 'disabled:cursor-not-allowed',
  ];

  // Outline variant styles
  const outlineStyles = outline
    ? [
        'bg-white dark:bg-black',
        isDelete ? 'border border-solid border-failure' : 'border border-solid border-[#D5D7DA]',
        isDelete && 'text-failure',
        isDelete
          ? 'enabled:hover:bg-failure_highlight enabled:hover:text-failure'
          : 'enabled:hover:bg-[#F5F5F5] dark:enabled:hover:bg-surface_gray_25 enabled:hover:text-text_gray_800',
      ]
    : [];

  // Text variant styles
  const textStyles = text
    ? [
        'p-0',
        // Base text color
        !className.includes('text-') && (isDelete ? 'text-failure' : 'text-text_gray_800'),
        // Hover text color
        !className.includes('hover:text-') &&
          (isDelete ? 'hover:text-failure_highlight' : 'hover:text-primary_highlight'),
        !className.includes('hover:opacity-') && 'hover:opacity-70',
        !className.includes('bg') && 'bg-transparent',
      ]
    : [];

  // Additional styles
  const additionalStyles = [
    hidden && 'hidden',
    !text && 'shadow-[0px_1px_2px_0px_#0A0D120D]',
    className, // Custom classes always come last to allow overrides
  ];

  // Combine all style arrays and filter out falsy values
  const combinedStyles = [
    ...layoutStyles,
    ...typographyStyles,
    ...colorStyles,
    ...hoverStyles,
    ...disabledStyles,
    ...outlineStyles,
    ...textStyles,
    ...additionalStyles,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (...args: any[]) => {
    if (analyticsEventName) {
      FeatureTrackingService.captureEvent(analyticsEventName, analyticsEventProperties);
    }

    onClick?.(...args);
  };

  return (
    <>
      {tooltipContent && (
        <div
          className={`absolute flex w-fit text-nowrap bg-surface_gray_200 rounded-lg p-2 -top-10 -left-1/2 ${
            showTooltip ? 'shadow-sm' : 'hidden'
          }`}
        >
          {tooltipContent}
        </div>
      )}

      <button
        type={type}
        onClick={handleClick}
        disabled={disabled}
        className={combinedStyles}
        style={style}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        {...props}
        {...analyticsAttributes}
      >
        {children}
      </button>
    </>
  );
};

export default Button;
